import {
  Button,
  Card,
  Col,
  Collapse,
  List,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  CalendarOutlined,
  CloudOutlined,
  DatabaseOutlined,
  DeleteColumnOutlined,
  EditOutlined,
  FieldTimeOutlined,
  FileOutlined,
  NumberOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  DataFeedConfig,
  DataFeedFileType,
} from '@cbinsights/externalapiservice/externalapiservice';

import { Link } from 'react-router-dom';
import { useListDataFeedConfig } from '../services/hooks';
import { DataFeedRoutes } from '../routesHelper';
import { DataFeedFileTypeLabels } from './DataFeedFiles';
import {
  FeedOptionalColumns,
  formatOptionalColumnCount,
} from './OptionalColumnsSummary';
import {
  DatasetLabels,
  fileTypeToNumber,
  isSupportedDataset,
} from './DataFeedForm';
import {
  FrequencyOptionLabels,
  isSupportedFrecuencyType,
} from './DataFeedFrequencyTabForm';
import {
  DestinationTypeLabels,
  isSupportedDestinationType,
} from './DataFeedDestinationTabForm';
import { DeleteDataFeedButton } from './DeleteDataFeedButton';
import { ToggleDataFeedStatusButton } from './ToggleDataFeedStatusButton';
import { validTabs } from './MultiSearchFilters';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const formatTabLabel = (tabValue: number): string => {
  const tab = [...validTabs].find(([, val]) => val === tabValue)?.[0] ?? '';
  return tab.charAt(0).toUpperCase() + tab.slice(1);
};

export const TeamDataFeedConfigList = ({ idTeam }: { idTeam: string }) => {
  const { isFetching, data: dataFeedConfigs } = useListDataFeedConfig(idTeam);
  const [modal, contextHolder] = Modal.useModal();

  const showOptionalColumns = (
    idDataFeed: number,
    fileType: DataFeedFileType,
    columns: string[]
  ) => {
    modal.info({
      getContainer: false,
      icon: null,
      title: (
        <>
          Optional columns
          <Text type="secondary" className="block text-sm font-normal">
            Feed #{idDataFeed} · {DataFeedFileTypeLabels[fileType]}
          </Text>
        </>
      ),
      width: 700,
      content: (
        <div className="mt-3 pt-3 border-t border-gray-200 max-h-[50vh] overflow-y-auto">
          <FeedOptionalColumns columns={columns} />
        </div>
      ),
    });
  };

  return (
    <>
      {contextHolder}
      <List
        data-test="permissions-list"
        itemLayout="horizontal"
        dataSource={dataFeedConfigs?.data_feed_configs || []}
        locale={{
          emptyText: 'No data feeds found for this team',
        }}
        loading={isFetching}
        style={{ marginBottom: 20 }}
        renderItem={(item: DataFeedConfig) => {
          return (
            <List.Item style={{ padding: 10 }}>
              <Card
                className="w-full"
                hoverable
                actions={[
                  <DeleteDataFeedButton feed={item} idTeam={idTeam} />,
                  <ToggleDataFeedStatusButton feed={item} idTeam={idTeam} />,
                  <Link
                    to={`${DataFeedRoutes.DataFeed}/${idTeam}/${DataFeedRoutes.EditDataFeed}/${item.id_data_feed}`}
                    key="edit-link"
                  >
                    <Button type="link">
                      <EditOutlined /> Edit Datafeed
                    </Button>
                  </Link>,
                ]}
              >
                <Row align="middle" gutter={8}>
                  <Col>
                    <Title level={4} className="text-blue-600 m-0">
                      {item.data_feed_name}
                    </Title>
                  </Col>
                  <Col>
                    <Tag
                      className="mb-2"
                      color={item.is_active ? 'green' : 'red'}
                    >
                      {item.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Text strong>
                      <NumberOutlined /> Feed:{' '}
                    </Text>
                    {item.id_data_feed}
                  </Col>
                  <Col span={8}>
                    <Text strong>
                      <ApiOutlined /> Client:{' '}
                    </Text>
                    {item.client_id}
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 10 }}>
                  <Col span={8}>
                    <Text strong>
                      <DatabaseOutlined /> Dataset:{' '}
                    </Text>
                    {isSupportedDataset(item.dataset) &&
                      DatasetLabels[item.dataset]}
                  </Col>
                  <Col span={8}>
                    <Text strong>
                      <FieldTimeOutlined /> Frequency:{' '}
                    </Text>
                    {isSupportedFrecuencyType(item.frequency) &&
                      FrequencyOptionLabels[item.frequency]}
                  </Col>
                  <Col span={8}>
                    <Text strong>
                      <CalendarOutlined /> Updated on:{' '}
                    </Text>
                    {new Date(item.last_update_time).toLocaleString('en-US')}
                  </Col>
                  <Col span={12}>
                    <Text strong>
                      <CloudOutlined /> Destination:{' '}
                    </Text>
                    {item?.destination?.destination_type !== undefined &&
                      isSupportedDestinationType(
                        item?.destination?.destination_type
                      ) &&
                      DestinationTypeLabels[
                        item?.destination?.destination_type
                      ]}
                  </Col>
                  <Col span={24}>
                    <Text strong>
                      <FileOutlined /> Files{' '}
                      {item?.deltas ? (
                        <span>(full and deltas files)</span>
                      ) : (
                        <span>(full files only)</span>
                      )}
                      :{' '}
                    </Text>
                    {item?.file_types?.map((el) => {
                      const entry =
                        item.optional_columns_by_file_type?.[
                          fileTypeToNumber(el)
                        ];
                      const columns =
                        entry && Object.keys(entry.columns).sort();
                      if (!columns) {
                        return <Tag key={el}>{DataFeedFileTypeLabels[el]}</Tag>;
                      }
                      const open = () =>
                        showOptionalColumns(item.id_data_feed, el, columns);
                      return (
                        <Tag
                          key={el}
                          role="button"
                          tabIndex={0}
                          className="cursor-pointer"
                          onClick={open}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              open();
                            }
                          }}
                        >
                          {DataFeedFileTypeLabels[el]}{' '}
                          <span className="text-blue-600">
                            · {formatOptionalColumnCount(columns.length)} ›
                          </span>
                        </Tag>
                      );
                    })}
                    {item?.search_config?.id_multi_search &&
                      item?.search_config?.id_tab && (
                        <Collapse bordered={false}>
                          <Panel
                            header={
                              <span>
                                <SearchOutlined /> Multi Search
                              </span>
                            }
                            key="1"
                          >
                            <Space direction="vertical">
                              <div>
                                <Text>Tab</Text>{' '}
                                <Tag>
                                  {formatTabLabel(item.search_config.id_tab)}
                                </Tag>
                              </div>
                              <div>
                                <Text>UUID</Text>{' '}
                                <Tag>{item.search_config.id_multi_search}</Tag>
                              </div>
                              {item.search_config?.top_n_orgs !== 0 && (
                                <div>
                                  <Text>Top X orgs</Text>{' '}
                                  <Tag>{item.search_config?.top_n_orgs}</Tag>
                                </div>
                              )}
                            </Space>
                          </Panel>
                        </Collapse>
                      )}
                    {item?.column_exclusions.length > 0 && (
                      <Collapse bordered={false}>
                        <Panel
                          header={
                            <span>
                              <DeleteColumnOutlined /> Excluded columns
                            </span>
                          }
                          key="1"
                        >
                          <Space direction="vertical">
                            {item?.column_exclusions?.map((el) => {
                              return (
                                <div key={el.file_type}>
                                  <Text>
                                    {DataFeedFileTypeLabels[el.file_type]}:
                                  </Text>{' '}
                                  {el.columns.map((col) => (
                                    <Tag key={col}>{col}</Tag>
                                  ))}
                                </div>
                              );
                            })}
                          </Space>
                        </Panel>
                      </Collapse>
                    )}
                  </Col>
                </Row>
              </Card>
            </List.Item>
          );
        }}
      />
    </>
  );
};
